import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Site-wide scroll smoothing.
 *
 * This is the cheapest premium signal on the page and the only one that lifts
 * every other scroll effect at once. Native wheel scroll advances in coarse,
 * variable jumps, so anything driven by scroll position — the header's progress
 * hairline, Workspace's parallax panel, the capability strip's scrub — inherits
 * that stepping no matter how carefully its own curve is authored. Lenis
 * interpolates the position between wheel events, so all of them get a
 * continuous input instead of a stuttering one.
 *
 * It drives the real scroll position rather than transforming a wrapper, which
 * is why `useScroll`, `useScrollScrub` and the CSS `animation-timeline: view()`
 * path in `parallaxDriver` keep working unchanged: they are all reading the
 * same `scrollY` they were before, just sampled more smoothly.
 *
 * THREE THINGS IT DELIBERATELY DOES NOT DO:
 *
 * 1. **Touch.** `syncTouch` stays off. Native momentum scrolling on iOS and
 *    Android is already better than anything re-implemented in JS, and
 *    hijacking it is the single most common way smooth-scroll libraries make a
 *    site feel worse on a phone than it did without them.
 * 2. **Reduced motion.** Under `prefers-reduced-motion` Lenis is never
 *    constructed. Smoothing is interpolated movement the visitor did not ask
 *    for, and the setting exists to say so. Scroll falls back to the browser's.
 * 3. **Outlive the route.** `destroy()` runs on unmount and the singleton is
 *    cleared. Lenis attaches wheel, touch and resize listeners plus an rAF
 *    loop; leaving one running behind an SPA navigation is a compounding leak.
 */

/**
 * The live instance, or null when smoothing is off.
 *
 * A module singleton because exactly one thing needs to reach it from outside —
 * `ScrollToTop`, which has to jump without animating through the whole page —
 * and threading a context through the tree for one imperative call would be
 * more moving parts than the problem has.
 */
let instance: Lenis | null = null;

export function getLenis(): Lenis | null {
  return instance;
}

/**
 * Jump to the top with no animation, through Lenis when it is running.
 *
 * Calling `window.scrollTo(0, 0)` while Lenis is active sets the browser's
 * position but not Lenis's internal target, so its next frame animates the page
 * back down to where the visitor had been. Route changes have to go through the
 * instance.
 */
export function jumpToTop(): void {
  if (instance) {
    instance.scrollTo(0, { immediate: true });
    return;
  }
  /* Explicitly instant. This branch is the reduced-motion path, where Lenis is
     absent and `html { scroll-behavior: smooth }` therefore applies — a bare
     `window.scrollTo(0, 0)` would animate the whole page back to the top on
     every route change, which is a long piece of motion for the one visitor who
     asked for none. */
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

export function SmoothScroll() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches) return;

    const lenis = new Lenis({
      /* Interpolation factor per frame. Lower is smoother and laggier; higher
         is more direct and steppier. Held just under Lenis's 0.1 default: the
         smoothing stays obvious enough to feel intentional, without the page
         arriving somewhere the pointer left a moment ago. Anything near 0.05
         reads as syrup on a page this tall. */
      lerp: 0.09,
      /* Wheel only. See note 1 above. */
      syncTouch: false,
      /* One rAF loop owned by Lenis, stopped by destroy(). */
      autoRaf: true,
    });

    instance = lenis;

    return () => {
      lenis.destroy();
      instance = null;
    };
  }, []);

  return null;
}
